import { Feather } from '@expo/vector-icons';
import { MeetingsModel, VisitsModel } from '@w3lcome/types';
import { Text } from '_/components';
import themes from '_/constants/themes';
import logSentryException from '_/helpers/logSentryException';
import { hostsApi } from '_/services/api';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, TouchableOpacity, ActivityIndicator } from 'react-native';
import { showMessage } from 'react-native-flash-message';

import vistsApi from '../../../services/api/visits';
import Invitee from './Invitee';
import styles from './styles';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

interface InviteeListProps {
  meeting: MeetingsModel;
}

export default function InviteeList({ meeting }: InviteeListProps) {
  const [expandedInvitees, setExpandedInvitees] = useState(false);
  const { t: translate } = useTranslation();

  const userId = useSelector((state: any) => state.user.id);

  function toggleExpandedInvitees() {
    setExpandedInvitees((prev) => !prev);
  }

  const { data: invitees = [], isLoading: loadingInvitees } = useQuery({
    queryKey: ['meetingInvitees', meeting?.id, userId],
    enabled: !!meeting?.id && expandedInvitees,
    staleTime: 1000 * 60 * 5,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async ({ queryKey }) => {
      const [, meetingId] = queryKey;

      try {
        const meetingVisits = await vistsApi.getVisits({
          meetingId,
        });

        let newInvitees = meetingVisits.data;

        if (meeting?.coHosts?.length === 0) {
          const meetingHosts = await hostsApi.getMeetingHosts(meetingId as string);

          if (meetingHosts?.length > 0) {
            const hosts = meetingHosts.map((host) => ({
              ...host.host,
              checkinAt: true,
            }));

            newInvitees = newInvitees.concat(hosts as unknown as ConcatArray<VisitsModel>);
          }
        }

        return newInvitees;
      } catch (error: any) {
        if (error?.response?.data?.code !== 400) {
          showMessage({
            message: translate('InviteeList.error'),
            type: 'danger',
          });

          logSentryException({
            error,
            file: 'InviteeList.js',
            message: 'Error at loadInvitees function',
          });
        }

        throw error;
      }
    },
  });

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.rowContainer} onPress={toggleExpandedInvitees}>
        <View>
          <Text style={styles.title}>{translate('meeting.invitees')}</Text>
        </View>

        <Feather name={expandedInvitees ? 'chevron-up' : 'chevron-down'} style={styles.icons} />
      </TouchableOpacity>

      {expandedInvitees && (
        <>
          {loadingInvitees && (
            <View style={{ flexDirection: 'row' }}>
              <Text style={{ marginRight: 5 }}>{translate('meeting.loadingInvitees')}</Text>
              <ActivityIndicator size="small" color={themes.colors.primary} />
            </View>
          )}

          {!loadingInvitees && invitees.length === 0 && (
            <Text>{translate('meeting.emptyInvitees')}</Text>
          )}

          {!loadingInvitees &&
            invitees.map((invitee) => <Invitee key={invitee?.id} invitee={invitee} />)}
        </>
      )}
    </View>
  );
}
